import {
  authenticatedFetch,
  getApiUri,
  getFlowUri,
  QueryFromVars,
  throwBody,
  throwHttpError,
} from "@/api/shared.ts";
import type {
  FlowGeneratorSession,
  FlowGeneratorSessionInterventionUpsert,
  HandbackTutor,
} from "@/types/flow.ts";

const JsonHeaders = { "Content-Type": "application/json" };

export interface DashboardFlowSession {
  id: number;
  lessonId: number;
  userId: number;
  flowGeneratorSessionId: number | null;
  firstName: string | null;
  lastName: string | null;
  yearGroup: string | null;
  quizName: string | null;
  lessonType: string | null;
  dateCreated: string;
  dateHelpRequested: string | null;
  dateProcessed: string | null;
  dateCompleted: string | null;
  isOnline: boolean;
  isHelpRequested: boolean;
  isInProgress: boolean;
  isProactiveIntervention: boolean;
  tutorUserId: number | null;
  tutorUsername: string | null;
  avgScore: number | null;
  percentageComplete: number | null;
  country: string | null;
  // Safety-pause shape (see isPausedForReview): an auto-moderated session the
  // AI just released back into the queue carries isAutoModerated=true, no
  // current tutor, and the AI tutor as previousTutorId. Optional — older
  // payloads omit them.
  isAutoModerated?: boolean | null;
  previousTutorId?: number | null;
}

export interface DashboardLessonStats {
  dateUpdated: string;
  onlineUsersCount: number;
  helpRequestCount: number;
  activeFlowSessionsCount: number;
  claimedCount: number;
  onlineTutorsCount: number;
  activeFlowSessions: Array<DashboardFlowSession>;
  tutorsOnline: Array<string>;
}

export const getLessonDashboardStats = async (
  priority?: RequestPriority,
): Promise<DashboardLessonStats> => {
  const response = await authenticatedFetch(getApiUri("/v4/admin/lesson-dashboard-stats"), {
    method: "GET",
    headers: JsonHeaders,
    priority: priority ?? "low",
  });

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};

interface BulkUpdateUserEventsBody {
  action: string;
  userIds?: Array<number>;
  schoolId?: number;
  source?: string;
  userRole?: string;
}

export const postBulkUpdateUserEvents = async ({
  action,
  userIds,
  schoolId,
  source = "EediFamily",
  userRole = "Student",
}: BulkUpdateUserEventsBody) => {
  const response = await authenticatedFetch(getApiUri("/v3/admin/events/bulk-queue"), {
    method: "POST",
    headers: JsonHeaders,
    body: JSON.stringify({
      action,
      userIds: userIds ?? [],
      source,
      schoolId,
      userRole,
    }),
  });

  if (!response.ok) {
    throw await throwBody(response);
  }
};

export const getFlowGeneratorSession = async (
  flowSessionId: number,
): Promise<FlowGeneratorSession> => {
  const response = await authenticatedFetch(
    getApiUri(`/v4/admin/flow-generator-sessions/${flowSessionId}`),
    {
      method: "GET",
      headers: JsonHeaders,
      priority: "high",
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};

export const postFlowTutorMessage = async (flowSessionId: number, input: string) => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/flow-generator-sessions/${flowSessionId}/tutor-message`),
    {
      method: "POST",
      headers: JsonHeaders,
      body: JSON.stringify({
        input: input,
      }),
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};

export const postFlowTutorFileUpload = async (
  flowSessionId: number,
  file: File,
  message: string,
) => {
  const formData = new FormData();
  formData.append("FormFile", file);
  formData.append("FileName", file.name);
  formData.append("IsPinned", "false");
  formData.append("Message", message);

  // Note: no Content-Type header here on purpose — the browser needs to set it
  // so the multipart boundary is included
  const response = await authenticatedFetch(
    getFlowUri(`/v1/flow-generator-sessions/${flowSessionId}/file-upload`),
    {
      method: "PUT",
      body: formData,
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};

export const postFlowTutorClaim = async (
  flowSessionId: number,
  isLLMAugmented: boolean,
  overrideClaim?: boolean,
  botModelId?: string,
): Promise<void> => {
  const query = QueryFromVars([
    { name: "isLLMAugmented", value: isLLMAugmented },
    { name: "botModelId", value: botModelId },
    { name: "isForceClaim", value: overrideClaim },
  ]);

  const response = await authenticatedFetch(
    getFlowUri(`/v1/flow-generator-sessions/${flowSessionId}/interventions/claim${query}`),
    {
      method: "POST",
      headers: JsonHeaders,
      priority: "high",
    },
  );

  if (!response.ok) {
    // HttpError so callers can branch on the status (400 = already claimed)
    // without parsing the body
    throw await throwHttpError(response);
  }
};

export const postFlowTutorUnclaim = async (
  flowSessionId: number,
  body: FlowGeneratorSessionInterventionUpsert,
) => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/flow-generator-sessions/${flowSessionId}/interventions/unclaim`),
    {
      method: "POST",
      headers: JsonHeaders,
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};

/**
 * Toggles AI auto-send on a session's open intervention.
 *
 * enabled=true → the AI re-takes the session (leaves the queue); refused with a
 * 409 while a flagged reply is still awaiting review.
 * enabled=false → manual pause: an AI-owned session drops into the ordinary
 * unclaimed queue with the AI unable to generate or send anything.
 *
 * 204 = success (idempotent). Errors are thrown as HttpError: 404 no open
 * intervention, 409 business conflicts as plain text (but the throttle filter
 * also 409s — see isThrottled409), 410 resolved elsewhere.
 */
export const postFlowAiAutoSend = async (
  flowSessionId: number,
  enabled: boolean,
): Promise<void> => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/flow-generator-sessions/${flowSessionId}/interventions/ai-auto-send`),
    {
      method: "POST",
      headers: JsonHeaders,
      body: JSON.stringify({ enabled }),
      priority: "high",
    },
  );

  if (!response.ok) {
    throw await throwHttpError(response);
  }
};

/**
 * Hands the session back to the AI tutor. The endpoint re-validates
 * everything server-side; a 400 is a BusinessException whose body is a
 * user-readable message (not configured / not the claiming tutor / human-only
 * student / AI not on duty) — surface it in a toast.
 */
export const postFlowTutorHandover = async (
  flowSessionId: number,
  tutorNotes: string | null,
): Promise<void> => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/flow-generator-sessions/${flowSessionId}/interventions/handover`),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ tutorNotes: tutorNotes?.trim() || null }),
      priority: "high",
    },
  );

  if (!response.ok) {
    throw await throwHttpError(response);
  }
};

export const handbackTutorQueryKey = (flowSessionId: number) =>
  ["handback-tutor", flowSessionId] as const;

/**
 * Whether this session can be handed back to the AI tutor.
 * 200 → applicable: show the "Hand back to {firstName}" button (isAvailable
 * false = the AI is not on duty right now — render it disabled).
 * 204 → null: not applicable (unclaimed, AI-owned, no open intervention, or
 * student opted out of AI) — hide the button.
 * Point-in-time: fetch on session-view load and refetch after any
 * claim/unclaim/handover, since ownership changes flip the answer.
 */
export const getFlowHandbackTutor = async (
  flowSessionId: number,
): Promise<HandbackTutor | null> => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/flow-generator-sessions/${flowSessionId}/interventions/ai-tutor-availability`),
    {
      method: "GET",
      headers: JsonHeaders,
    },
  );

  if (!response.ok) {
    throw await throwHttpError(response);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
};

export const putFlowTutorNote = async (
  body: FlowGeneratorSessionInterventionUpsert,
  flowSessionId: number,
) => {
  const response = await authenticatedFetch(
    getFlowUri(`/v1/flow-generator-sessions/${flowSessionId}/interventions`),
    {
      method: "PUT",
      headers: JsonHeaders,
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};
